import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { useAlert } from '../context/AlertContext';
import { BACKEND_URL } from '../config';
import './OBSControl.css';

const socket = io(BACKEND_URL);

function OBSControl() {
  const [connected, setConnected] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [scenes, setScenes] = useState([]);
  const [currentScene, setCurrentScene] = useState('');
  const [sources, setSources] = useState([]);
  const [audioSources, setAudioSources] = useState([]);
  const [stats, setStats] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [debugLog, setDebugLog] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [showStreamSettings, setShowStreamSettings] = useState(false);
  const [streamSettings, setStreamSettings] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [autoRefreshPreview, setAutoRefreshPreview] = useState(true);
  const [previewFps, setPreviewFps] = useState(0);
  const previewPendingRef = React.useRef(false);
  const fpsCountRef = React.useRef(0);
  const fpsTimerRef = React.useRef(Date.now());
  const [camDelays, setCamDelays] = useState({});
  const camSources = ['Cam 3', 'Feste-Cam', 'Handy-Cam'];
  const alert = useAlert();

  // Quick Access Panel
  const DEFAULT_QUICKBAR = [
    { id: 1, action: 'stream_toggle', label: 'Stream' },
    { id: 2, action: 'record_toggle', label: 'Record' },
    { id: 3, action: null, label: 'Slot 3' },
    { id: 4, action: null, label: 'Slot 4' },
    { id: 5, action: null, label: 'Slot 5' },
    { id: 6, action: null, label: 'Slot 6' },
    { id: 7, action: null, label: 'Slot 7' },
    { id: 8, action: null, label: 'Slot 8' },
  ];
  const [quickbar, setQuickbar] = useState(() => {
    try {
      const saved = localStorage.getItem('obs_quickbar');
      return saved ? JSON.parse(saved) : DEFAULT_QUICKBAR;
    } catch { return DEFAULT_QUICKBAR; }
  });
  const [editingSlot, setEditingSlot] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const addDebugLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLog(prev => [...prev.slice(-9), `[${timestamp}] ${message}`]);
    console.log('[OBS Debug]', message);
  };

  // Save quickbar to localStorage
  useEffect(() => {
    localStorage.setItem('obs_quickbar', JSON.stringify(quickbar));
  }, [quickbar]);

  const executeAction = (action) => {
    if (!action) return;
    if (action === 'stream_toggle') { toggleStreaming(); return; }
    if (action === 'record_toggle') { toggleRecording(); return; }
    if (action === 'pause_start') { socket.emit('obs:startPause'); return; }
    if (action === 'pause_stop') { socket.emit('obs:stopPause'); return; }
    if (action === 'skip_video') { handleSkipVideo(); return; }
    if (action.startsWith('scene:')) {
      switchScene(action.replace('scene:', ''));
    }
  };

  const saveSlot = (id, updates) => {
    setQuickbar(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    setEditingSlot(null);
  };

  const getActionLabel = (action) => {
    if (!action) return 'Leer';
    if (action === 'stream_toggle') return streaming ? '⏹ Stop Stream' : '▶ Start Stream';
    if (action === 'record_toggle') return recording ? '⏹ Stop Rec' : '⏺ Start Rec';
    if (action === 'pause_start') return '⏸ Pause';
    if (action === 'pause_stop') return '▶ Weiter';
    if (action === 'skip_video') return '⏭ Skip';
    if (action.startsWith('scene:')) return action.replace('scene:', '');
    return action;
  };

  const getActionIcon = (action) => {
    if (!action) return '+';
    if (action === 'stream_toggle') return streaming ? '⏹' : '▶';
    if (action === 'record_toggle') return recording ? '⏹' : '⏺';
    if (action === 'pause_start') return '⏸';
    if (action === 'pause_stop') return '▶';
    if (action === 'skip_video') return '⏭';
    if (action.startsWith('scene:')) return '🎬';
    return '▶';
  };

  const getSlotClass = (action) => {
    if (!action) return 'slot-empty';
    if (action === 'stream_toggle') return streaming ? 'slot-active-danger' : 'slot-active-gold';
    if (action === 'record_toggle') return recording ? 'slot-active-danger' : 'slot-active';
    if (action.startsWith('scene:')) {
      const sceneName = action.replace('scene:', '');
      return sceneName === currentScene ? 'slot-active-gold' : 'slot-active';
    }
    return 'slot-active';
  };

  useEffect(() => {
    addDebugLog('Component mounted, setting up socket listeners');

    socket.on('obs:connected', () => {
      addDebugLog('✓ OBS connected successfully!');
      if (!connected) {
        setConnected(true);
        setErrorMessage('');
        alert.success('OBS verbunden!');
      }
      loadOBSData();
    });

    socket.on('obs:disconnected', (data) => {
      addDebugLog(`✗ OBS disconnected: ${data?.error || 'Unknown error'}`);
      if (connected) {
        setConnected(false);
        if (data && data.error) {
          setErrorMessage(data.details || data.error);
          alert.error(`OBS Fehler: ${data.error}`);
        }
      }
    });

    socket.on('obs:status', (data) => {
      addDebugLog(`Status update: Streaming=${data.streaming}, Recording=${data.recording}`);
      setStreaming(data.streaming);
      setRecording(data.recording);
      setStats(data.stats || {});
    });

    socket.on('obs:scenes', (data) => {
      addDebugLog(`Scenes loaded: ${data.scenes.length} scenes, current: ${data.currentScene}`);
      setScenes(data.scenes);
      setCurrentScene(data.currentScene);
    });

    socket.on('obs:sources', (data) => {
      addDebugLog(`Sources loaded: ${data.length} sources`);
      setSources(data);
    });

    socket.on('obs:audioSources', (data) => {
      addDebugLog(`Audio sources loaded: ${data.length} audio sources`);
      setAudioSources(data);
    });

    socket.on('obs:streamSettings', (data) => {
      addDebugLog('Stream settings loaded');
      setStreamSettings(data);
    });

    socket.on('obs:screenshot', (data) => {
      setScreenshot(data.imageData);
      // Count FPS
      fpsCountRef.current++;
      const now = Date.now();
      if (now - fpsTimerRef.current >= 1000) {
        setPreviewFps(fpsCountRef.current);
        fpsCountRef.current = 0;
        fpsTimerRef.current = now;
      }
      // Immediately request next frame if auto-refresh is on
      previewPendingRef.current = false;
      if (autoRefreshPreview) {
        previewPendingRef.current = true;
        socket.emit('obs:getScreenshot');
      }
    });

    socket.on('obs:camDelayUpdated', (data) => {
      setCamDelays(prev => ({ ...prev, [data.sourceName]: data.delayMs }));
    });

    socket.on('obs:camDelayError', (data) => {
      alert.error(`Delay Fehler (${data.sourceName}): ${data.error}`);
    });

    addDebugLog('Sending connection request...');
    socket.emit('obs:connect');

    // Poll status every 2 seconds (not screenshots - those are driven by response)
    const interval = setInterval(() => {
      if (connected) {
        socket.emit('obs:getStatus');
      }
    }, 2000);

    // Kick off the preview loop once connected
    if (connected && autoRefreshPreview) {
      previewPendingRef.current = true;
      socket.emit('obs:getScreenshot');
    }

    return () => {
      clearInterval(interval);
      socket.off('obs:connected');
      socket.off('obs:disconnected');
      socket.off('obs:status');
      socket.off('obs:scenes');
      socket.off('obs:sources');
      socket.off('obs:audioSources');
      socket.off('obs:screenshot');
      socket.off('obs:camDelayUpdated');
      socket.off('obs:camDelayError');
    };
  }, [connected, alert]);

  const loadOBSData = () => {
    socket.emit('obs:getScenes');
    socket.emit('obs:getStatus');
    // Cam Delays laden
    camSources.forEach(src => socket.emit('obs:getCamDelay', { sourceName: src }));
  };

  const adjustCamDelay = (sourceName, deltaMs) => {
    socket.emit('obs:adjustCamDelay', { sourceName, deltaMs });
  };

  const updateCamSourceName = (index, name) => {
    // Namen sind fest konfiguriert
  };

  const switchScene = (sceneName) => {
    socket.emit('obs:setScene', sceneName);
    setCurrentScene(sceneName);
  };

  const toggleStreaming = () => {
    if (streaming) {
      socket.emit('obs:stopStreaming');
    } else {
      socket.emit('obs:startStreaming');
    }
  };

  const toggleRecording = () => {
    if (recording) {
      socket.emit('obs:stopRecording');
    } else {
      socket.emit('obs:startRecording');
    }
  };

  const toggleSource = (sceneItemId, visible) => {
    socket.emit('obs:toggleSource', { sceneItemId, visible: !visible });
    // Update local state
    setSources(sources.map(s => 
      s.sceneItemId === sceneItemId ? { ...s, sceneItemEnabled: !visible } : s
    ));
  };

  const adjustSourceVolume = (sourceName, volume) => {
    socket.emit('obs:setVolume', { sourceName, volume });
    // Update local state
    setAudioSources(audioSources.map(s => 
      s.inputName === sourceName ? { ...s, inputVolume: volume } : s
    ));
  };

  const muteSource = (sourceName, muted) => {
    socket.emit('obs:setMute', { sourceName, muted });
    // Update local state
    setAudioSources(audioSources.map(s => 
      s.inputName === sourceName ? { ...s, inputMuted: muted } : s
    ));
  };

  const openSourceSettings = (source) => {
    setSelectedSource(source);
  };

  const closeSourceSettings = () => {
    setSelectedSource(null);
  };

  const updateSourceSettings = (settings) => {
    socket.emit('obs:updateSourceSettings', {
      sourceName: selectedSource.sourceName,
      settings
    });
    closeSourceSettings();
  };

  const startPauseMode = () => {
    socket.emit('obs:startPause');
  };

  const stopPauseMode = () => {
    socket.emit('obs:stopPause');
  };

  const openStreamSettings = () => {
    socket.emit('obs:getStreamSettings');
    setShowStreamSettings(true);
  };

  const closeStreamSettings = () => {
    setShowStreamSettings(false);
  };

  const saveStreamSettings = (settings) => {
    socket.emit('obs:updateStreamSettings', settings);
    closeStreamSettings();
    alert.success('Stream Settings gespeichert');
  };

  const handleSkipVideo = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/obs-agent/skip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        alert.success('Video übersprungen!');
      } else {
        const data = await response.json();
        alert.error(data.error || 'Fehler beim Überspringen');
      }
    } catch (error) {
      console.error('Error skipping video:', error);
      alert.error('Fehler beim Überspringen');
    }
  };

  if (!connected) {
    return (
      <div className="obs-control">
        <div className="page-header">
          <h2>OBS Control Panel</h2>
          <div className="connection-status">
            <span className="status-dot"></span>
            <span>Verbindung wird hergestellt...</span>
          </div>
        </div>
        
        <div className="connection-error">
          <h2>⚠ OBS nicht verbunden</h2>
          
          {errorMessage && (
            <div style={{ 
              background: '#eb0400', 
              color: '#fff', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontFamily: 'monospace',
              fontSize: '13px'
            }}>
              <strong>Fehler:</strong> {errorMessage}
            </div>
          )}
          
          <p>Stelle sicher, dass:</p>
          <ul>
            <li>OBS läuft</li>
            <li>OBS WebSocket Plugin installiert ist (Tools → WebSocket Server Settings)</li>
            <li>WebSocket Server in OBS aktiviert ist</li>
            <li>Port ist 4444 (oder dein konfigurierter Port)</li>
            <li>Das Passwort korrekt ist</li>
            <li>Die Verbindungsdaten in Settings → Twitch Main korrekt sind</li>
          </ul>
          
          <button 
            className="btn-success" 
            onClick={() => {
              addDebugLog('Manual reconnect triggered');
              socket.emit('obs:connect');
            }}
            style={{ marginTop: '20px' }}
          >
            🔄 Erneut verbinden
          </button>

          {/* Debug Log */}
          <div style={{
            marginTop: '30px',
            background: '#0e0e10',
            border: '1px solid #3a3a3d',
            borderRadius: '8px',
            padding: '15px',
            maxHeight: '300px',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: '14px', marginBottom: '10px', color: '#9147ff' }}>Debug Log:</h3>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#adadb8' }}>
              {debugLog.length === 0 ? (
                <div>Warte auf Logs...</div>
              ) : (
                debugLog.map((log, i) => (
                  <div key={i} style={{ marginBottom: '5px' }}>{log}</div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="obs-control">
      <div className="page-header">
        <h2>OBS Control Panel</h2>
        <div className="connection-status">
          <span className="status-dot connected"></span>
          <span>Verbunden</span>
        </div>
      </div>

      <div className="control-grid">
        {/* ===== QUICK ACCESS PANEL ===== */}
        <div className="control-section quickbar-section">
          <h3>
            Schnellzugriff
            <button
              className={`edit-mode-btn ${isEditMode ? 'active' : ''}`}
              onClick={() => { setIsEditMode(!isEditMode); setEditingSlot(null); }}
            >
              {isEditMode ? '✓ Fertig' : '✏ Bearbeiten'}
            </button>
          </h3>

          <div className="quickbar">
            {quickbar.map((slot) => (
              <div key={slot.id} className="quickbar-slot-wrapper">
                {isEditMode ? (
                  /* EDIT MODE: show config */
                  <div
                    className={`quickbar-btn slot-editing`}
                    onClick={() => setEditingSlot(editingSlot === slot.id ? null : slot.id)}
                  >
                    <span className="qb-icon">✏</span>
                    <span className="qb-label">{slot.label || getActionLabel(slot.action)}</span>
                  </div>
                ) : (
                  /* NORMAL MODE: execute action */
                  <button
                    className={`quickbar-btn ${getSlotClass(slot.action)}`}
                    onClick={() => executeAction(slot.action)}
                    disabled={!slot.action}
                  >
                    <span className="qb-icon">{getActionIcon(slot.action)}</span>
                    <span className="qb-label">{slot.label || getActionLabel(slot.action)}</span>
                  </button>
                )}

                {/* Inline editor for this slot */}
                {isEditMode && editingSlot === slot.id && (
                  <div className="slot-editor">
                    <div className="slot-editor-field">
                      <label>Label</label>
                      <input
                        type="text"
                        defaultValue={slot.label || ''}
                        placeholder="Button-Name..."
                        onBlur={(e) => saveSlot(slot.id, { label: e.target.value })}
                      />
                    </div>
                    <div className="slot-editor-field">
                      <label>Aktion</label>
                      <select
                        value={slot.action || ''}
                        onChange={(e) => {
                          const action = e.target.value || null;
                          const autoLabel = action ? (
                            action === 'stream_toggle' ? 'Stream' :
                            action === 'record_toggle' ? 'Record' :
                            action === 'pause_start' ? 'Pause' :
                            action === 'pause_stop' ? 'Weiter' :
                            action === 'skip_video' ? 'Skip' :
                            action.startsWith('scene:') ? action.replace('scene:', '') : action
                          ) : 'Leer';
                          saveSlot(slot.id, { action, label: autoLabel });
                        }}
                      >
                        <option value="">— Leer —</option>
                        <option value="stream_toggle">▶/⏹ Stream Toggle</option>
                        <option value="record_toggle">⏺/⏹ Record Toggle</option>
                        <option value="pause_start">⏸ Pause starten</option>
                        <option value="pause_stop">▶ Pause stoppen</option>
                        <option value="skip_video">⏭ Video skippen</option>
                        <optgroup label="── Scenes ──">
                          {scenes.map(s => (
                            <option key={s.sceneName} value={`scene:${s.sceneName}`}>
                              🎬 {s.sceneName}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* OBS Preview */}
        <div className="control-section preview-section">
          <h3>
            OBS Preview
            <div className="preview-controls">
              {autoRefreshPreview && (
                <span className="fps-badge">{previewFps} FPS</span>
              )}
              <label className="auto-refresh-toggle">
                <input 
                  type="checkbox" 
                  checked={autoRefreshPreview}
                  onChange={(e) => {
                    setAutoRefreshPreview(e.target.checked);
                    if (e.target.checked) {
                      previewPendingRef.current = true;
                      socket.emit('obs:getScreenshot');
                    } else {
                      previewPendingRef.current = false;
                    }
                  }}
                />
                <span>Live</span>
              </label>
            </div>
          </h3>
          <div className="obs-preview">
            {screenshot ? (
              <img src={screenshot} alt="OBS Preview" />
            ) : (
              <div className="preview-placeholder">
                <p>📹</p>
                <p>Warte auf Preview...</p>
              </div>
            )}
          </div>
          <button 
            className="btn-secondary refresh-btn"
            onClick={() => socket.emit('obs:getScreenshot')}
          >
            🔄 Manuell aktualisieren
          </button>
        </div>

        {/* Stream & Recording Controls */}
        <div className="control-section">
          <h3>Stream & Recording</h3>
          <div className="control-buttons">
            <button 
              className={streaming ? 'btn-danger' : 'btn-success'}
              onClick={toggleStreaming}
            >
              {streaming ? '⏹ Stop Stream' : '▶ Start Stream'}
            </button>
            <button 
              className={recording ? 'btn-danger' : 'btn-secondary'}
              onClick={toggleRecording}
            >
              {recording ? '⏹ Stop Recording' : '⏺ Start Recording'}
            </button>
            <button 
              className="btn-secondary"
              onClick={openStreamSettings}
            >
              ⚙ Stream Settings
            </button>
          </div>
          
          {(streaming || recording) && (
            <div className="stream-stats">
              {streaming && <p>Stream Time: {formatTime(stats.streamTime || 0)}</p>}
              <p>FPS: {stats.fps || 0}</p>
              <p>CPU: {stats.cpu || 0}%</p>
              {streaming && stats.kbitsPerSec > 0 && <p>Bitrate: {stats.kbitsPerSec} kb/s</p>}
            </div>
          )}
        </div>

        {/* Pause Mode Controls */}
        <div className="control-section">
          <h3>Quick Actions</h3>
          <div className="control-buttons">
            <button className="btn-success" onClick={startPauseMode}>
              ⏸ Pause starten
            </button>
            <button className="btn-secondary" onClick={stopPauseMode}>
              ▶ Pause stoppen
            </button>
          </div>
        </div>

        {/* Cam Render Delay */}
        <div className="control-section">
          <h3>📷 Cam Render Delay (Async)</h3>
          <p style={{ color: '#adadb8', fontSize: '12px', marginBottom: '12px' }}>
            Asynchrone Renderverzögerung. +200ms fügt einen Filter hinzu, -200ms entfernt einen.
          </p>
          {camSources.map((camName) => (
            <div key={camName} className="cam-delay-row">
              <span className="cam-name-label">{camName}</span>
              <div className="cam-delay-controls">
                <button
                  className="btn-danger cam-delay-btn"
                  onClick={() => adjustCamDelay(camName, -200)}
                >
                  -200ms
                </button>
                <span className="cam-delay-value">
                  {camDelays[camName] !== undefined ? `${camDelays[camName]}ms` : '0ms'}
                </span>
                <button
                  className="btn-success cam-delay-btn"
                  onClick={() => adjustCamDelay(camName, 200)}
                >
                  +200ms
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* 24/7 Stream Controls */}
        <div className="control-section">
          <h3>24/7 Stream Control</h3>
          <div className="control-buttons">
            <button className="btn-success" onClick={() => socket.emit('obs247:startStream')}>
              ▶ Start 24/7 Stream
            </button>
            <button className="btn-danger" onClick={() => socket.emit('obs247:stopStream')}>
              ⏹ Stop 24/7 Stream
            </button>
            <button className="btn-secondary" onClick={handleSkipVideo}>
              ⏭ Skip Video
            </button>
          </div>
        </div>

        {/* Scenes */}
        <div className="control-section scenes-section">
          <h3>Scenes</h3>
          <div className="scenes-grid">
            {scenes.map((scene) => (
              <button
                key={scene.sceneName}
                className={`scene-button ${scene.sceneName === currentScene ? 'active' : ''}`}
                onClick={() => switchScene(scene.sceneName)}
              >
                <span className="scene-icon">🎬</span>
                <span className="scene-name">{scene.sceneName}</span>
                {scene.sceneName === currentScene && <span className="scene-live-badge">LIVE</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Sources */}
        <div className="control-section sources-section">
          <h3>Sources (Current Scene)</h3>
          <div className="sources-list">
            {sources.length === 0 ? (
              <p style={{ color: '#adadb8', textAlign: 'center', padding: '20px' }}>
                Keine Sources in dieser Scene
              </p>
            ) : (
              sources.map((source) => (
                <div key={source.sceneItemId} className="source-item">
                  <div className="source-info">
                    <span className="source-name">{source.sourceName}</span>
                    <span className="source-type">{source.inputKind || 'Unknown'}</span>
                  </div>
                  <div className="source-controls">
                    <button
                      className="btn-secondary"
                      onClick={() => openSourceSettings(source)}
                      title="Einstellungen"
                    >
                      ⚙
                    </button>
                    <button
                      className={source.sceneItemEnabled ? 'btn-success' : 'btn-secondary'}
                      onClick={() => toggleSource(source.sceneItemId, source.sceneItemEnabled)}
                    >
                      {source.sceneItemEnabled ? '👁 Visible' : '👁‍🗨 Hidden'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Audio Mixer */}
        <div className="control-section audio-section">
          <h3>Audio Mixer</h3>
          <div className="audio-list">
            {audioSources.length === 0 ? (
              <p style={{ color: '#adadb8', textAlign: 'center', padding: '20px' }}>
                Keine Audio-Quellen gefunden
              </p>
            ) : (
              audioSources.map((source) => (
                <div key={source.inputName} className="audio-item">
                  <div className="audio-header">
                    <div>
                      <span className="audio-name">{source.inputName}</span>
                      <span className="source-type" style={{ display: 'block', marginTop: '4px' }}>
                        {source.inputKind}
                      </span>
                    </div>
                    <button
                      className={source.inputMuted ? 'btn-danger' : 'btn-secondary'}
                      onClick={() => muteSource(source.inputName, !source.inputMuted)}
                    >
                      {source.inputMuted ? '🔇' : '🔊'}
                    </button>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1000"
                    value={source.inputVolume || 0}
                    onChange={(e) => adjustSourceVolume(source.inputName, parseInt(e.target.value))}
                    className="volume-slider"
                  />
                  <span className="volume-label">{Math.round(source.inputVolume / 10)}%</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Source Settings Modal */}
      {selectedSource && (
        <SourceSettingsModal
          source={selectedSource}
          onClose={closeSourceSettings}
          onSave={updateSourceSettings}
        />
      )}

      {/* Stream Settings Modal */}
      {showStreamSettings && streamSettings && (
        <StreamSettingsModal
          settings={streamSettings}
          onClose={closeStreamSettings}
          onSave={saveStreamSettings}
        />
      )}
    </div>
  );
}

function SourceSettingsModal({ source, onClose, onSave }) {
  const [settings, setSettings] = useState({});

  const handleSave = () => {
    onSave(settings);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Source Settings: {source.sourceName}</h3>
        
        <div className="form-group">
          <label>Source Type</label>
          <input type="text" value={source.inputKind || 'Unknown'} disabled />
        </div>

        {source.inputKind === 'browser_source' && (
          <>
            <div className="form-group">
              <label>URL</label>
              <input
                type="text"
                placeholder="https://..."
                onChange={(e) => setSettings({ ...settings, url: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Width</label>
              <input
                type="number"
                placeholder="1920"
                onChange={(e) => setSettings({ ...settings, width: parseInt(e.target.value) })}
              />
            </div>
            <div className="form-group">
              <label>Height</label>
              <input
                type="number"
                placeholder="1080"
                onChange={(e) => setSettings({ ...settings, height: parseInt(e.target.value) })}
              />
            </div>
          </>
        )}

        {source.inputKind === 'ffmpeg_source' && (
          <>
            <div className="form-group">
              <label>Local File</label>
              <input
                type="text"
                placeholder="C:/Videos/video.mp4"
                onChange={(e) => setSettings({ ...settings, local_file: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  onChange={(e) => setSettings({ ...settings, looping: e.target.checked })}
                />
                Loop
              </label>
            </div>
          </>
        )}

        {source.inputKind === 'image_source' && (
          <div className="form-group">
            <label>Image File</label>
            <input
              type="text"
              placeholder="C:/Images/image.png"
              onChange={(e) => setSettings({ ...settings, file: e.target.value })}
            />
          </div>
        )}

        <div className="modal-actions">
          <button type="button" onClick={onClose}>Abbrechen</button>
          <button type="button" className="btn-success" onClick={handleSave}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default OBSControl;

function StreamSettingsModal({ settings, onClose, onSave }) {
  const [videoSettings, setVideoSettings] = useState({
    outputWidth: settings.video?.outputWidth || 1920,
    outputHeight: settings.video?.outputHeight || 1080,
    fpsNumerator: settings.video?.fpsNumerator || 60,
    fpsDenominator: settings.video?.fpsDenominator || 1
  });

  const [streamSettings, setStreamSettings] = useState({
    bwtest: settings.stream?.bwtest || false,
    server: settings.stream?.server || '',
    key: settings.stream?.key || ''
  });

  const [encoderSettings, setEncoderSettings] = useState({
    videoBitrate: settings.encoder?.videoBitrate || 6000,
    audioBitrate: settings.encoder?.audioBitrate || 160,
    preset: settings.encoder?.preset || 'veryfast',
    profile: settings.encoder?.profile || 'high'
  });

  const handleSave = () => {
    onSave({
      video: videoSettings,
      stream: streamSettings,
      encoder: encoderSettings
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <h3>Stream & Video Settings</h3>
        
        <h4 style={{ marginTop: '20px', marginBottom: '10px', fontSize: '14px', color: '#9147ff' }}>
          Video Settings
        </h4>
        
        <div className="form-group">
          <label>Output Resolution</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="number"
              value={videoSettings.outputWidth}
              onChange={(e) => setVideoSettings({ ...videoSettings, outputWidth: parseInt(e.target.value) })}
              placeholder="Width"
              style={{ flex: 1 }}
            />
            <span style={{ alignSelf: 'center' }}>×</span>
            <input
              type="number"
              value={videoSettings.outputHeight}
              onChange={(e) => setVideoSettings({ ...videoSettings, outputHeight: parseInt(e.target.value) })}
              placeholder="Height"
              style={{ flex: 1 }}
            />
          </div>
          <small>Standard: 1920×1080 (Full HD), 1280×720 (HD)</small>
        </div>

        <div className="form-group">
          <label>FPS (Frames per Second)</label>
          <select
            value={videoSettings.fpsNumerator}
            onChange={(e) => setVideoSettings({ ...videoSettings, fpsNumerator: parseInt(e.target.value), fpsDenominator: 1 })}
          >
            <option value="30">30 FPS</option>
            <option value="60">60 FPS</option>
            <option value="120">120 FPS</option>
          </select>
        </div>

        <h4 style={{ marginTop: '20px', marginBottom: '10px', fontSize: '14px', color: '#9147ff' }}>
          Stream Settings
        </h4>

        <div className="form-group">
          <label>Server URL</label>
          <input
            type="text"
            value={streamSettings.server}
            onChange={(e) => setStreamSettings({ ...streamSettings, server: e.target.value })}
            placeholder="rtmp://..."
          />
          <small>Twitch: rtmp://live.twitch.tv/app/</small>
        </div>

        <div className="form-group">
          <label>Stream Key</label>
          <input
            type="password"
            value={streamSettings.key}
            onChange={(e) => setStreamSettings({ ...streamSettings, key: e.target.value })}
            placeholder="live_..."
          />
          <small>Dein Stream Key von Twitch/YouTube</small>
        </div>

        <h4 style={{ marginTop: '20px', marginBottom: '10px', fontSize: '14px', color: '#9147ff' }}>
          Encoder Settings (Bitrate & Quality)
        </h4>

        <div className="form-group">
          <label>Video Bitrate (kb/s)</label>
          <input
            type="number"
            value={encoderSettings.videoBitrate}
            onChange={(e) => setEncoderSettings({ ...encoderSettings, videoBitrate: parseInt(e.target.value) })}
            placeholder="6000"
          />
          <small>Empfohlen: 1080p60 = 6000 kb/s, 1080p30 = 4500 kb/s, 720p60 = 4500 kb/s, 720p30 = 3000 kb/s</small>
        </div>

        <div className="form-group">
          <label>Audio Bitrate (kb/s)</label>
          <select
            value={encoderSettings.audioBitrate}
            onChange={(e) => setEncoderSettings({ ...encoderSettings, audioBitrate: parseInt(e.target.value) })}
          >
            <option value="96">96 kb/s</option>
            <option value="128">128 kb/s</option>
            <option value="160">160 kb/s (Empfohlen)</option>
            <option value="192">192 kb/s</option>
            <option value="256">256 kb/s</option>
            <option value="320">320 kb/s</option>
          </select>
        </div>

        <div className="form-group">
          <label>Encoder Preset</label>
          <select
            value={encoderSettings.preset}
            onChange={(e) => setEncoderSettings({ ...encoderSettings, preset: e.target.value })}
          >
            <option value="ultrafast">Ultrafast (niedrigste CPU, niedrigste Qualität)</option>
            <option value="superfast">Superfast</option>
            <option value="veryfast">Veryfast (Empfohlen)</option>
            <option value="faster">Faster</option>
            <option value="fast">Fast</option>
            <option value="medium">Medium (höchste CPU, höchste Qualität)</option>
          </select>
          <small>Schnellere Presets = weniger CPU, aber größere Dateien</small>
        </div>

        <div className="form-group">
          <label>H.264 Profile</label>
          <select
            value={encoderSettings.profile}
            onChange={(e) => setEncoderSettings({ ...encoderSettings, profile: e.target.value })}
          >
            <option value="baseline">Baseline</option>
            <option value="main">Main</option>
            <option value="high">High (Empfohlen)</option>
          </select>
        </div>

        <div className="modal-actions">
          <button type="button" onClick={onClose}>Abbrechen</button>
          <button type="button" className="btn-success" onClick={handleSave}>Speichern</button>
        </div>
      </div>
    </div>
  );
}
